<template>
  <notifications
    :ignore-duplicates="true"
    animation-type="css"
    :position="position"
    v-bind="$attrs">
    <template #body="{ item, close }">
      <div v-if="isAppNotification(item)" class="app-notification" :class="item.data.appClasses">
        <v-icon class="type-icon">{{ item.data.icon }}</v-icon>
        <p class="text-h6 toast-notification-title" v-if="item.title">
          {{ item.title }}
        </p>
        <p class="message ma-0 pr-3 toast-notification-body" v-html="item.text"></p>
        <button class="close" @click="close">
          <v-icon>mdi-close</v-icon>
        </button>
        <a
          class="message ma-0 font-size-x-small font-weight-light"
          v-if="item.data.actionFn && item.data.actionText"
          @click="item.data.actionFn">
          {{ item.data.actionText }}
        </a>
      </div>
    </template>
  </notifications>
</template>

<script setup lang="ts">
import { defineProps } from 'vue';
import { Notifications } from '@kyvg/vue3-notification';
import { isArray, isObject } from 'lodash';
import type { NotifyType } from '@/lib/notify.ts';

type AppNotification = {
  title?: string;
  text: string;
  data: AppNotificationData;
  type?: NotifyType;
  duration?: number;
  speed?: number;
  clean?: boolean;
};

type AppNotificationData = {
  icon?: string;
  appClasses?: string;
  actionText?: string | null;
  actionFn?: (() => void) | null;
};

const isAppNotificationData = (data: unknown): data is AppNotificationData => {
  if (!isObject(data) || isArray(data)) return false;

  const obj = data as Record<string, unknown>;

  const isIconInvalid = 'icon' in obj && typeof obj.icon !== 'string';
  const isAppClassesInvalid = 'appClasses' in obj && typeof obj.appClasses !== 'string';
  const isActionTextInvalid =
    'actionText' in obj && obj.actionText !== null && typeof obj.actionText !== 'string';
  const isActionFnInvalid =
    'actionFn' in obj && obj.actionFn !== null && typeof obj.actionFn !== 'function';

  return !(isIconInvalid || isAppClassesInvalid || isActionTextInvalid || isActionFnInvalid);
};

const isValidNotifyType = (type: unknown): type is NotifyType => {
  return typeof type === 'string' && ['success', 'error', 'info', 'warning'].includes(type);
};

const isAppNotification = (notification: unknown): notification is AppNotification => {
  if (!isObject(notification) || isArray(notification)) return false;

  const obj = notification as Record<string, unknown>;

  const isTextInvalid = !('text' in obj) || typeof obj.text !== 'string';
  const isTitleInvalid = 'title' in obj && typeof obj.title !== 'string';
  const isDataInvalid = !('data' in obj) || !isAppNotificationData(obj.data);
  const isTypeInvalid = 'type' in obj && !isValidNotifyType(obj.type);
  const isDurationInvalid = 'duration' in obj && typeof obj.duration !== 'number';
  const isSpeedInvalid = 'speed' in obj && typeof obj.speed !== 'number';
  const isCleanInvalid = 'clean' in obj && typeof obj.clean !== 'boolean';

  return !(
    isTextInvalid ||
    isTitleInvalid ||
    isDataInvalid ||
    isTypeInvalid ||
    isDurationInvalid ||
    isSpeedInvalid ||
    isCleanInvalid
  );
};

const { position } = defineProps({
  position: {
    type: String,
    required: false,
    default: 'bottom center'
  }
});
</script>
